import { mdiChevronLeft } from "@mdi/js";
import type { ReactNode } from "react";
import { Icon } from "./Icon.tsx";

/**
 * The white band under the app bar.
 *
 * Every screen states the same four things in the same place: where you are,
 * what this page is, the facts that qualify it, and what you can do to it. The
 * band is the only part of a page allowed to carry page-level actions, so a
 * button never has to compete with content for the reader's attention.
 */
export function PageHeader({
  back,
  title,
  subtitle,
  meta,
  metaLabel,
  actions,
  children,
}: {
  back?: { label: string; onClick: () => void };
  title: string;
  subtitle?: ReactNode;
  meta?: { label: string; value: ReactNode; tone?: "overdue" }[];
  /** Names the meta row as a landmark, for pages where it is the summary. */
  metaLabel?: string;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="border-b border-border bg-card px-4 py-3.5 sm:px-7 sm:py-4">
      {back ? (
        <button
          className="-ml-1 flex min-h-11 items-center gap-1 text-[12px] text-muted-foreground hover:text-primary sm:min-h-0"
          type="button"
          onClick={back.onClick}
        >
          <Icon path={mdiChevronLeft} size={16} />
          <span>{back.label}</span>
        </button>
      ) : null}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-5">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-[26px]">
            {title}
          </h1>
          {subtitle ? <p className="mt-1 text-[13px] text-muted-foreground">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>
      {meta && meta.length > 0 ? (
        <section aria-label={metaLabel}>
          <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1">
            {meta.map((entry) => (
              <div className="flex flex-col gap-px" key={entry.label}>
                <dt className="meta-label">{entry.label}</dt>
                <dd className={`meta-value ${entry.tone === "overdue" ? "text-destructive" : ""}`}>
                  {entry.value}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      {children}
    </div>
  );
}

/** A white card with an optional titled head, used across every panel. */
export function Card({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`overflow-hidden rounded-lg border border-border bg-card ${className ?? ""}`}
    >
      {title ? (
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <h2 className="text-[15px] font-bold text-foreground">{title}</h2>
          <div className="flex-1" />
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}
